import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UploadSimple, Sparkle, WarningCircle, CheckCircle, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAiFileParse } from '@/hooks/useAiFileParse';
import { confidenceRing, ConfidenceBadge } from '@/components/upload/confidence';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuotationPdfUploadDialog = ({ open, onOpenChange, onSuccess }) => {
  const { getAuthHeader } = useAuth();
  const fileInputRef = useRef(null);
  const { uploading, parse } = useAiFileParse('/quotations/parse-pdf');

  const [step, setStep] = useState('upload'); // upload | review
  const [saving, setSaving] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [vendorName, setVendorName] = useState('');
  const [items, setItems] = useState([]);

  const resetAll = () => {
    setStep('upload');
    setSaving(false);
    setParseResult(null);
    setVendorName('');
    setItems([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = (nextOpen) => {
    if (!nextOpen) resetAll();
    onOpenChange(nextOpen);
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    const data = await parse(file);
    if (!data) return;
    setParseResult(data);
    setVendorName(data.vendor_name || '');
    // Initialize per-item editable state
    setItems(
      (data.items || []).map((it, idx) => ({
        key: `item-${idx}`,
        include: !!it.matched_requisition_id, // default include only matched items
        requisition_id: it.matched_requisition_id || '',
        matched_label: it.matched_requisition_label || '',
        reagent_name: it.reagent_name || '',
        catalogue_number: it.catalogue_number || '',
        price: it.price || 0,
        delivery_timeline: it.delivery_timeline || '',
        payment_terms: it.payment_terms || '',
        confidence: it.confidence || {},
      }))
    );
    setStep('review');
    toast.success(`Extracted ${data.items?.length || 0} line item(s) from PDF`);
  };

  const updateItem = (key, patch) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  };

  const handleSave = async () => {
    const toSave = items.filter((it) => it.include);
    if (toSave.length === 0) {
      toast.error('Select at least one item to save');
      return;
    }
    // Validate every included row has a matched requisition + vendor + price > 0
    for (const it of toSave) {
      if (!it.requisition_id) {
        toast.error(`Row "${it.reagent_name || 'unknown'}" needs a matched requisition`);
        return;
      }
      if (!vendorName.trim()) {
        toast.error('Vendor name is required');
        return;
      }
      if (!(it.price > 0)) {
        toast.error(`Row "${it.reagent_name}" needs a valid price`);
        return;
      }
      if (!it.delivery_timeline.trim() || !it.payment_terms.trim()) {
        toast.error(`Row "${it.reagent_name}" needs delivery timeline and payment terms`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        pdf_file_id: parseResult.pdf_file_id,
        quotations: toSave.map((it) => ({
          requisition_id: it.requisition_id,
          vendor_name: vendorName.trim(),
          price: parseFloat(it.price),
          delivery_timeline: it.delivery_timeline,
          payment_terms: it.payment_terms,
          pdf_file_id: parseResult.pdf_file_id,
          source: 'pdf_parsed',
        })),
      };
      const res = await axios.post(`${API}/quotations/bulk`, payload, {
        headers: getAuthHeader(),
      });
      toast.success(`Saved ${res.data.length} quotation(s) from PDF`);
      resetAll();
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save quotations');
    } finally {
      setSaving(false);
    }
  };

  const openReqs = parseResult?.open_requisitions || [];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="pdf-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkle size={22} weight="duotone" className="text-accent-brand" />
            Import Quotation from PDF
          </DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <div className="py-8">
            <div className="border-2 border-dashed border-border-default rounded-lg p-10 text-center bg-surface">
              <UploadSimple size={40} className="text-text-tertiary mx-auto mb-3" weight="duotone" />
              <p className="text-text-primary font-medium mb-1">Upload a vendor quotation PDF</p>
              <p className="text-sm text-text-tertiary mb-6">
                Our AI will extract line items (reagent, catalogue #, price, delivery, payment terms) and match them
                to your open requisitions.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={handleFileSelected}
                data-testid="pdf-file-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-accent-brand hover:bg-accent-brand-hover text-white"
                data-testid="choose-pdf-button"
              >
                {uploading ? 'Parsing PDF (10–30s)…' : 'Choose PDF file'}
              </Button>
              <p className="text-xs text-text-tertiary mt-4">Max 15 MB. Text-based or scanned PDFs both supported.</p>
            </div>
          </div>
        )}

        {step === 'review' && parseResult && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-surface rounded">
              <div className="md:col-span-2 space-y-1">
                <Label htmlFor="vendor-name-review">Vendor Name (applies to all line items)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="vendor-name-review"
                    value={vendorName}
                    onChange={(e) => setVendorName(e.target.value)}
                    className={confidenceRing(parseResult.vendor_confidence)}
                    data-testid="review-vendor-name"
                  />
                  <ConfidenceBadge level={parseResult.vendor_confidence} />
                </div>
              </div>
              <div className="flex items-end">
                <div className="text-sm">
                  <p className="text-text-tertiary">Source PDF</p>
                  <p className="text-text-primary font-medium truncate">{parseResult.original_filename}</p>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-text-primary">
                  Extracted line items ({items.length})
                </h3>
                <p className="text-xs text-text-tertiary">
                  Amber-highlighted fields have low AI confidence — please verify.
                </p>
              </div>

              <div className="border border-border-default rounded overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface border-b border-border-default">
                    <tr>
                      <th className="p-3 text-left w-10">Save</th>
                      <th className="p-3 text-left">Match to Requisition</th>
                      <th className="p-3 text-left">Reagent / Cat #</th>
                      <th className="p-3 text-left w-28">Price (₹)</th>
                      <th className="p-3 text-left w-40">Delivery</th>
                      <th className="p-3 text-left w-40">Payment Terms</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.key} className="border-b border-border-default align-top" data-testid={`item-row-${it.key}`}>
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={it.include}
                            onChange={(e) => updateItem(it.key, { include: e.target.checked })}
                            className="w-4 h-4"
                            data-testid={`include-${it.key}`}
                          />
                        </td>
                        <td className="p-3 min-w-[220px]">
                          <Select
                            value={it.requisition_id || 'unmatched'}
                            onValueChange={(v) => updateItem(it.key, { requisition_id: v === 'unmatched' ? '' : v })}
                          >
                            <SelectTrigger
                              className={!it.requisition_id ? 'ring-2 ring-amber-400 border-amber-400' : ''}
                              data-testid={`match-select-${it.key}`}
                            >
                              <SelectValue placeholder="Select requisition" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unmatched">— Not matched —</SelectItem>
                              {openReqs.map((r) => (
                                <SelectItem key={r.id} value={r.id}>
                                  {r.reagent_name} · {r.catalogue_number}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {!it.requisition_id && (
                            <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                              <WarningCircle size={12} weight="fill" /> No match — pick manually or uncheck
                            </p>
                          )}
                          {it.requisition_id && it.matched_label && (
                            <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                              <CheckCircle size={12} weight="fill" /> Auto-matched
                            </p>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="space-y-1">
                            <Input
                              value={it.reagent_name}
                              onChange={(e) => updateItem(it.key, { reagent_name: e.target.value })}
                              className={`h-8 ${confidenceRing(it.confidence?.reagent_name)}`}
                              placeholder="Reagent name"
                              data-testid={`reagent-${it.key}`}
                            />
                            <Input
                              value={it.catalogue_number}
                              onChange={(e) => updateItem(it.key, { catalogue_number: e.target.value })}
                              className={`h-8 font-mono text-xs ${confidenceRing(it.confidence?.catalogue_number)}`}
                              placeholder="Catalogue #"
                              data-testid={`catalogue-${it.key}`}
                            />
                          </div>
                        </td>
                        <td className="p-3">
                          <Input
                            type="number"
                            step="0.01"
                            value={it.price}
                            onChange={(e) => updateItem(it.key, { price: e.target.value })}
                            className={`h-8 ${confidenceRing(it.confidence?.price)}`}
                            data-testid={`price-${it.key}`}
                          />
                        </td>
                        <td className="p-3">
                          <Input
                            value={it.delivery_timeline}
                            onChange={(e) => updateItem(it.key, { delivery_timeline: e.target.value })}
                            className={`h-8 ${confidenceRing(it.confidence?.delivery_timeline)}`}
                            placeholder="e.g. 7-10 days"
                            data-testid={`delivery-${it.key}`}
                          />
                        </td>
                        <td className="p-3">
                          <Input
                            value={it.payment_terms}
                            onChange={(e) => updateItem(it.key, { payment_terms: e.target.value })}
                            className={`h-8 ${confidenceRing(it.confidence?.payment_terms)}`}
                            placeholder="e.g. Net 30"
                            data-testid={`payment-${it.key}`}
                          />
                        </td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr>
                        <td colSpan="6" className="p-6 text-center text-text-tertiary">
                          No line items detected in this PDF.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-between items-center pt-2 border-t">
              <Button variant="outline" onClick={resetAll} data-testid="upload-another-btn">
                <X size={16} className="mr-1" /> Upload another PDF
              </Button>
              <div className="flex items-center gap-3">
                <p className="text-sm text-text-tertiary">
                  {items.filter((i) => i.include && i.requisition_id).length} of {items.length} ready to save
                </p>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-accent-brand hover:bg-accent-brand-hover text-white"
                  data-testid="save-quotations-btn"
                >
                  {saving ? 'Saving…' : 'Save Quotations'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuotationPdfUploadDialog;
